/**
 * MTNCarbonMarkets.js — Carbon Markets & Monetisation
 * 7-section institutional workspace. Conservative telecom caveats.
 */
import React, { useState, useEffect } from "react";
import { useMTN } from "@/context/MTNContext";
import client from "@/api/client";
import { toast } from "sonner";
import { Loader, AlertTriangle, ChevronDown, ChevronUp, Download } from "lucide-react";

const fmtN=(v,u="")=>{if(!v&&v!==0)return"—";const n=Number(v);if(n>=1e6)return`${(n/1e6).toFixed(1)}M ${u}`.trim();if(n>=1e3)return`${(n/1e3).toFixed(0)}K ${u}`.trim();return`${n.toLocaleString()} ${u}`.trim();};
const fmtU=v=>{if(!v&&v!==0)return"—";const n=Number(v);if(n>=1e9)return`$${(n/1e9).toFixed(1)}B`;if(n>=1e6)return`$${(n/1e6).toFixed(1)}M`;if(n>=1e3)return`$${(n/1e3).toFixed(0)}K`;return`$${n}`;};

const CARBON_SECTIONS=[
  {id:"funnel",label:"Carbon Funnel"},
  {id:"pathways",label:"Pathway Qualification"},
  {id:"registry",label:"Registry & Methodology"},
  {id:"article6",label:"Article 6 Matrix"},
  {id:"pdd",label:"PDD Pipeline"},
  {id:"readiness",label:"PDD Readiness"},
  {id:"structuring",label:"Asset Structuring"},
  {id:"commercial",label:"Commercialisation"},
];

export default function MTNCarbonMarkets() {
  const { selectedPeriod, loading } = useMTN();
  const [carbonIntel, setCarbonIntel] = useState(null);
  const [qualData, setQualData] = useState(null);
  const [loadingC, setLoadingC] = useState(false);
  const [activeSection, setActiveSection] = useState("funnel");
  const [expandedPath, setExpandedPath] = useState(null);
  const [expandedPDD, setExpandedPDD] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!selectedPeriod) return;
    setLoadingC(true);
    Promise.all([
      client.get(`/mtn/carbon-intelligence/${selectedPeriod}`),
      client.get(`/mtn/qualification/${selectedPeriod}`),
    ]).then(([ci, qs]) => { setCarbonIntel(ci.data); setQualData(qs.data); })
    .catch(() => toast.error("Carbon intelligence load failed"))
    .finally(() => setLoadingC(false));
  }, [selectedPeriod]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/mtn/pdf/carbon-markets/${selectedPeriod}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("decarboniq_token")}` } });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `MTN_CarbonMarkets_${selectedPeriod}.pdf`; a.click();
      URL.revokeObjectURL(url); toast.success("Downloaded");
    } catch { toast.error("Download failed"); }
    finally { setDownloading(false); }
  };

  if (loading || loadingC) return <div style={{padding:40,color:"#64748b",fontSize:12}}>{loadingC?"Loading carbon intelligence…":"Loading…"}</div>;

  const sig = carbonIntel?.ssot_signals || {};
  const qs = qualData?.group_qualification || {};

  return (
    <div style={{padding:"24px 28px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
        <div>
          <span style={{fontSize:9,fontWeight:800,color:"#FFCB00",letterSpacing:".08em",textTransform:"uppercase",background:"#FFCB0015",border:"1px solid #FFCB0030",padding:"2px 8px",borderRadius:4}}>MTN Nigeria</span>
          <h1 style={{fontSize:20,fontWeight:900,color:"#f1f5f9",marginTop:6}}>Carbon Markets & Monetisation</h1>
          <p style={{fontSize:11,color:"#475569"}}>Institutional carbon origination workspace · Conservative telecom caveats · SSOT-derived</p>
        </div>
        <button onClick={downloadPdf} disabled={downloading}
          style={{display:"flex",alignItems:"center",gap:6,background:"#1d4ed8",color:"#fff",border:"none",borderRadius:6,padding:"8px 14px",fontSize:10,fontWeight:700,cursor:"pointer"}}
          data-testid="download-carbon-markets">
          {downloading?<Loader size={11} className="animate-spin"/>:<Download size={11}/>} Carbon Markets Report
        </button>
      </div>

      {/* SSOT Signal Bar */}
      {carbonIntel && (
        <div style={{background:"#0a0f1e",border:"1px solid #1e293b",borderRadius:8,padding:14,marginBottom:14}} data-testid="ssot-signal-bar">
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
            <span style={{fontSize:8,fontWeight:700,color:"#475569",textTransform:"uppercase",letterSpacing:".08em"}}>SSOT Signals — Carbon Intelligence</span>
            <div style={{display:"flex",gap:6}}>
              <span style={{fontSize:8,background:"#78350f40",color:"#f59e0b",border:"1px solid #f59e0b40",padding:"2px 8px",borderRadius:4,fontWeight:700}}>Indicative Only</span>
              <span style={{fontSize:8,background:"#1e293b",color:"#94a3b8",padding:"2px 8px",borderRadius:4,fontWeight:700}}>Not Bankable</span>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:8}}>
            {[["Data Confidence",`${(sig.conf_avg||0).toFixed(0)}/100`,(sig.conf_avg||0)>=65?"#22c55e":"#ef4444","VVB threshold ≥65"],
              ["T1/T2 Coverage",`${sig.t1_share_pct||0}%`,(sig.t1_share_pct||0)>=70?"#22c55e":"#f59e0b","Target ≥70%"],
              ["Monetisable Vol.",fmtN(sig.monetisable,"tCO₂e"),"#60a5fa","Subject to validation"],
              ["VCM Projects",`${sig.vcm_projects||0}`,"#22c55e","PoA structure needed"],
              ["Art.6 Projects",`${sig.art6_projects||0}`,"#a78bfa","Host auth. required"],
              ["Solar Identified",sig.has_solar?"Yes":"No",sig.has_solar?"#22c55e":"#f59e0b","Primary VCM lever"],
            ].map(([l,v,c,n])=>(
              <div key={l} style={{border:"1px solid #1e293b",borderRadius:6,padding:"8px 10px"}}>
                <p style={{fontSize:7,color:"#475569",textTransform:"uppercase",marginBottom:3}}>{l}</p>
                <p style={{fontSize:12,fontWeight:900,color:c}}>{v}</p>
                <p style={{fontSize:7,color:"#334155"}}>{n}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sub-nav */}
      {carbonIntel && (
        <div style={{display:"flex",flexWrap:"wrap",gap:3,background:"#0a0f1e",border:"1px solid #1e293b",borderRadius:8,padding:6,marginBottom:14}} data-testid="carbon-subnav">
          {CARBON_SECTIONS.map(s=>(
            <button key={s.id} onClick={()=>setActiveSection(s.id)}
              style={{padding:"5px 10px",borderRadius:5,border:"none",cursor:"pointer",fontSize:9,fontWeight:600,
                background:activeSection===s.id?"#FFCB00":"transparent",color:activeSection===s.id?"#000":"#475569"}}
              data-testid={`carbon-section-${s.id}`}>{s.label}
            </button>
          ))}
        </div>
      )}

      {/* FUNNEL */}
      {carbonIntel && activeSection==="funnel" && (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}} data-testid="section-funnel">
          <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16}}>
            <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:12}}>Carbon Qualification Funnel — Telecom PoA</p>
            {[["A. Theoretical",qs.theoretical_total,"#374151"],["B. Technically Feasible",qs.feasible_total,"#0891b2"],
              ["C. Potentially Creditable",qs.creditable_total,"#d97706"],["D. Potentially Monetisable",qs.monetisable_total,"#22c55e"]
            ].map(([tier,val,col])=>{
              const pct=Math.min(100,(val/Math.max(qs.theoretical_total,1))*100);
              return(
                <div key={tier} style={{marginBottom:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                    <span style={{fontSize:9,fontWeight:700,color:col}}>{tier}</span>
                    <span style={{fontSize:9,fontWeight:700,color:col}}>{fmtN(val,"tCO₂e/yr")} ({pct.toFixed(0)}%)</span>
                  </div>
                  <div style={{height:8,background:"#1e293b",borderRadius:4,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${pct}%`,background:col+"40",borderLeft:`3px solid ${col}`,borderRadius:4}}/>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {[["Conservative ($8/t)",fmtU((qs.monetisable_total||0)*8)+"/yr","#64748b","Conservative floor"],
              ["Base ($15/t)",fmtU((qs.monetisable_total||0)*15)+"/yr","#22c55e","Conservative VCM"],
              ["Optimistic ($25/t)",fmtU((qs.monetisable_total||0)*25)+"/yr","#f59e0b","Favourable market"],
            ].map(([k,v,c,s])=>(
              <div key={k} style={{background:"#0d1423",border:`1px solid ${c}25`,borderRadius:8,padding:12}}>
                <p style={{fontSize:8,color:"#64748b"}}>{k}</p>
                <p style={{fontSize:16,fontWeight:900,color:c,marginTop:2}}>{v}</p>
                <p style={{fontSize:8,color:"#475569"}}>{s}</p>
              </div>
            ))}
            <div style={{background:"#7c2d1220",border:"1px solid #ef444430",borderRadius:8,padding:12}}>
              <p style={{fontSize:8,fontWeight:700,color:"#ef4444",marginBottom:4}}>Telecom Carbon Credit Caution</p>
              <p style={{fontSize:9,color:"#f87171"}}>Telecom carbon credits require aggregated PoA structure for distributed tower fleet. Not straightforward — methodology complexity high. Values indicative only.</p>
            </div>
          </div>
        </div>
      )}

      {/* PATHWAYS */}
      {carbonIntel && activeSection==="pathways" && (
        <div style={{display:"flex",flexDirection:"column",gap:10}} data-testid="section-pathways">
          {(carbonIntel.pathway_qualification||[]).map(pw=>(
            <div key={pw.short} style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,overflow:"hidden"}}>
              <div style={{padding:14}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:10}}>
                  <div>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                      <span style={{fontSize:11,fontWeight:800,color:"#f1f5f9"}}>{pw.name}</span>
                      <span style={{fontSize:8,fontWeight:700,padding:"2px 7px",borderRadius:4,border:`1px solid ${pw.accent}40`,background:pw.accent+"15",color:pw.accent}}>{pw.eligibility}</span>
                      <span style={{fontSize:8,fontWeight:700,padding:"2px 7px",borderRadius:4,background:pw.status==="Active"?"#14532d30":pw.status==="Recommended"?"#1e3a5f30":"#1e293b",color:pw.status==="Active"?"#22c55e":pw.status==="Recommended"?"#60a5fa":"#64748b"}}>{pw.status}</span>
                    </div>
                    <p style={{fontSize:9,color:"#64748b"}}>{pw.basis}</p>
                  </div>
                  <div style={{textAlign:"right",flexShrink:0,marginLeft:12}}>
                    <p style={{fontSize:8,color:"#475569"}}>Indicative Volume</p>
                    <p style={{fontSize:14,fontWeight:900,color:pw.accent}}>{fmtN(pw.volume_tco2e,"tCO₂e")}</p>
                    <p style={{fontSize:8,color:"#475569"}}>~{pw.timeline_months}mo to issuance</p>
                  </div>
                </div>
                <div style={{marginBottom:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                    <span style={{fontSize:8,color:"#475569"}}>Pathway Readiness</span>
                    <span style={{fontSize:9,fontWeight:700,color:pw.accent}}>{pw.readiness_pct}%</span>
                  </div>
                  <div style={{height:4,background:"#1e293b",borderRadius:2,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${pw.readiness_pct}%`,background:pw.accent,borderRadius:2}}/>
                  </div>
                </div>
                <button onClick={()=>setExpandedPath(expandedPath===pw.short?null:pw.short)}
                  style={{display:"flex",alignItems:"center",gap:5,fontSize:9,color:"#64748b",background:"none",border:"none",cursor:"pointer"}}
                  data-testid={`pathway-expand-${pw.short}`}>
                  {expandedPath===pw.short?<ChevronUp size={10}/>:<ChevronDown size={10}/>}
                  {expandedPath===pw.short?"Hide":"Show"} blockers ({pw.blockers?.length||0})
                </button>
              </div>
              {expandedPath===pw.short && (
                <div style={{background:"#060c1a",borderTop:"1px solid #1e293b",padding:12}}>
                  <p style={{fontSize:8,fontWeight:700,color:"#475569",textTransform:"uppercase",marginBottom:8}}>Blockers & Required Actions</p>
                  {(pw.blockers||[]).map((b,i)=>(
                    <div key={i} style={{display:"flex",gap:6,marginBottom:5}}>
                      <AlertTriangle size={9} color="#f59e0b" style={{flexShrink:0,marginTop:1}}/>
                      <p style={{fontSize:9,color:"#94a3b8"}}>{b}</p>
                    </div>
                  ))}
                  {(pw.short==="Art.6.2"||pw.short==="Art.6.4")&&(
                    <div style={{background:"#7f1d1d20",border:"1px solid #ef444430",borderRadius:6,padding:10,marginTop:8}}>
                      <p style={{fontSize:8,fontWeight:700,color:"#ef4444",marginBottom:3}}>Sovereign Process Mandatory</p>
                      <p style={{fontSize:9,color:"#f87171"}}>NOT financeable until NCCC formally authorises. Exclude from all debt structuring and DSCR models.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* REGISTRY */}
      {carbonIntel && activeSection==="registry" && (
        <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,overflow:"hidden"}} data-testid="section-registry">
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead><tr style={{borderBottom:"1px solid #1e293b",background:"#060c1a"}}>
              {["Project","Registry","Methodology","Family","Stage","Compat.","Pathway","Next Action"].map(h=>(
                <th key={h} style={{textAlign:"left",padding:"8px 10px",fontSize:8,color:"#475569",fontWeight:600}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {(carbonIntel.registry_methodology||[]).map((r,i)=>(
                <tr key={i} style={{borderBottom:"1px solid #0f172a"}} data-testid={`registry-row-${i}`}>
                  <td style={{padding:"7px 10px",fontSize:9,color:"#f1f5f9",fontWeight:600,maxWidth:120}}><p style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.project}</p></td>
                  <td style={{padding:"7px 10px",fontSize:9,fontWeight:700,color:"#60a5fa",whiteSpace:"nowrap"}}>{r.registry}</td>
                  <td style={{padding:"7px 10px",fontSize:8,color:"#94a3b8"}}>{r.methodology}</td>
                  <td style={{padding:"7px 10px",fontSize:8,color:"#64748b"}}>{r.family}</td>
                  <td style={{padding:"7px 10px"}}><span style={{fontSize:8,background:"#1e293b",color:"#94a3b8",padding:"2px 5px",borderRadius:3}}>{r.stage}</span></td>
                  <td style={{padding:"7px 10px"}}>
                    <div style={{display:"flex",alignItems:"center",gap:5}}>
                      <div style={{width:28,height:4,background:"#1e293b",borderRadius:2,overflow:"hidden"}}><div style={{height:"100%",width:`${r.compat_score}%`,background:r.compat_color}}/></div>
                      <span style={{fontSize:9,fontWeight:700,color:r.compat_color}}>{r.compat_score}</span>
                    </div>
                  </td>
                  <td style={{padding:"7px 10px"}}><span style={{fontSize:8,fontWeight:700,padding:"2px 5px",borderRadius:3,background:r.pathway==="art6"?"#4c1d9530":"#14532d30",color:r.pathway==="art6"?"#a78bfa":"#22c55e"}}>{r.pathway==="art6"?"Art.6":"VCM"}</span></td>
                  <td style={{padding:"7px 10px",fontSize:8,color:"#60a5fa"}}>{r.compat_score>=70?"Commission baseline+DOE":r.compat_score>=45?"Upgrade evidence quality":"Formal methodology review"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ARTICLE 6 */}
      {carbonIntel && activeSection==="article6" && (
        <div style={{display:"flex",flexDirection:"column",gap:10}} data-testid="section-article6">
          <div style={{background:"#7f1d1d20",border:"1px solid #ef444430",borderRadius:8,padding:14}}>
            <div style={{display:"flex",gap:8,alignItems:"flex-start"}}>
              <AlertTriangle size={14} color="#ef4444" style={{flexShrink:0,marginTop:2}}/>
              <div>
                <p style={{fontSize:10,fontWeight:700,color:"#ef4444",marginBottom:4}}>Critical Caveat — Article 6 Pathways</p>
                <p style={{fontSize:9,color:"#f87171"}}>Art.6.2 and Art.6.4 are NOT financeable until: (a) NCCC sovereign authorisation granted; (b) corresponding adjustment approved; (c) bilateral framework established; (d) DNA engagement completed. Do NOT include in DSCR or financing models without formal sovereign authorisation.</p>
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <p style={{fontSize:8,color:"#64748b"}}>Overall Readiness</p>
                <p style={{fontSize:22,fontWeight:900,color:"#f59e0b"}}>{carbonIntel.article6_overall}/100</p>
                <p style={{fontSize:8,color:"#f59e0b",fontWeight:700}}>Early Stage Only</p>
              </div>
            </div>
          </div>
          <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,overflow:"hidden"}}>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead><tr style={{borderBottom:"1px solid #1e293b",background:"#060c1a"}}>
                {["Requirement","Status","Readiness","Notes","Next Action","Who Acts"].map(h=>(
                  <th key={h} style={{textAlign:"left",padding:"8px 10px",fontSize:8,color:"#475569",fontWeight:600}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {(carbonIntel.article6_matrix||[]).map((row,i)=>(
                  <tr key={i} style={{borderBottom:"1px solid #0f172a"}} data-testid={`art6-row-${i}`}>
                    <td style={{padding:"7px 10px",fontSize:9,fontWeight:600,color:"#f1f5f9"}}>{row.requirement}</td>
                    <td style={{padding:"7px 10px"}}><span style={{fontSize:8,fontWeight:700,padding:"2px 6px",borderRadius:3,background:row.color+"20",color:row.color}}>{row.status}</span></td>
                    <td style={{padding:"7px 10px"}}><div style={{display:"flex",alignItems:"center",gap:4}}><div style={{width:32,height:4,background:"#1e293b",borderRadius:2,overflow:"hidden"}}><div style={{height:"100%",width:`${row.score}%`,background:row.color}}/></div><span style={{fontSize:8,fontWeight:700,color:row.color}}>{row.score}</span></div></td>
                    <td style={{padding:"7px 10px",fontSize:8,color:"#64748b",maxWidth:160}}>{row.note}</td>
                    <td style={{padding:"7px 10px",fontSize:8,color:"#60a5fa"}}>{row.score<20?"Initiate DNA+NCCC":row.score<40?"Commission alignment":"Advance engagement"}</td>
                    <td style={{padding:"7px 10px",fontSize:8,color:"#94a3b8",whiteSpace:"nowrap"}}>{row.requirement?.includes("DNA")?"Nigeria DNA/NCCC":row.requirement?.includes("Host")?"Nigeria NCCC":"NCCC/Supervisory"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* PDD PIPELINE */}
      {carbonIntel && activeSection==="pdd" && (
        <div style={{display:"flex",flexDirection:"column",gap:10}} data-testid="section-pdd">
          {(carbonIntel.pdd_pipeline||[]).length===0 && <div style={{background:"#0d1423",borderRadius:8,padding:24,textAlign:"center"}}><p style={{color:"#475569",fontSize:12}}>No carbon-pathway projects. Assign carbon_pathway in Data Workspace.</p></div>}
          {(carbonIntel.pdd_pipeline||[]).map(p=>(
            <div key={p.project_id} style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,overflow:"hidden"}} data-testid={`pdd-card-${p.project_id}`}>
              <div style={{padding:14}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:10}}>
                  <div>
                    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
                      <span style={{fontSize:10,fontWeight:800,color:"#f1f5f9"}}>{p.name}</span>
                      <span style={{fontSize:8,fontWeight:700,padding:"2px 6px",borderRadius:3,border:"1px solid",borderColor:p.pathway==="art6"?"#a78bfa40":"#22c55e40",background:p.pathway==="art6"?"#4c1d9520":"#14532d20",color:p.pathway==="art6"?"#a78bfa":"#22c55e"}}>{p.pathway==="art6"?"Art.6 — Sovereign Auth. Required":"VCM"}</span>
                    </div>
                    <p style={{fontSize:9,color:"#475569"}}>Stage {p.current_stage}/11: <span style={{fontWeight:700,color:"#94a3b8"}}>{p.stage_label}</span> · Readiness: {p.readiness_pct}%</p>
                  </div>
                  <div style={{textAlign:"right",flexShrink:0}}>
                    <p style={{fontSize:8,color:"#475569"}}>Issuance Potential</p>
                    <p style={{fontSize:14,fontWeight:900,color:"#60a5fa"}}>{fmtN(p.issue_potential_tco2e,"tCO₂e")}</p>
                    <p style={{fontSize:8,color:"#475569"}}>{p.timeline_yr}yr to issuance</p>
                  </div>
                </div>
                <div style={{display:"flex",gap:1,marginBottom:6}}>
                  {(p.all_stages||[]).map((_,si)=>(
                    <div key={si} style={{flex:1,height:4,borderRadius:1,background:si<p.current_stage?"#22c55e":si===p.current_stage?"#60a5fa":"#1e293b"}}/>
                  ))}
                </div>
                <div style={{background:"#1e3a5f30",border:"1px solid #60a5fa30",borderRadius:5,padding:8,marginBottom:8}}>
                  <p style={{fontSize:8,fontWeight:700,color:"#60a5fa",marginBottom:2}}>Next Required Action</p>
                  <p style={{fontSize:9,color:"#f1f5f9"}}>{p.next_action}</p>
                </div>
                <button onClick={()=>setExpandedPDD(expandedPDD===p.project_id?null:p.project_id)}
                  style={{display:"flex",alignItems:"center",gap:5,fontSize:9,color:"#64748b",background:"none",border:"none",cursor:"pointer"}}
                  data-testid={`pdd-expand-${p.project_id}`}>
                  {expandedPDD===p.project_id?<ChevronUp size={10}/>:<ChevronDown size={10}/>}
                  {expandedPDD===p.project_id?"Hide":"Show"} blockers ({p.blockers?.length||0})
                </button>
              </div>
              {expandedPDD===p.project_id && (
                <div style={{background:"#060c1a",borderTop:"1px solid #1e293b",padding:12}}>
                  {(p.blockers||[]).map((b,i)=>(<div key={i} style={{display:"flex",gap:5,marginBottom:4}}><AlertTriangle size={9} color="#f59e0b" style={{flexShrink:0}}/><p style={{fontSize:9,color:"#94a3b8"}}>{b}</p></div>))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* PDD READINESS */}
      {carbonIntel && activeSection==="readiness" && (
        <div style={{display:"flex",flexDirection:"column",gap:10}} data-testid="section-readiness">
          <div style={{background:"#0a0f1e",border:"1px solid #1e293b",borderRadius:8,padding:14}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",marginBottom:2}}>PDD Readiness — Six-Dimension Framework</p>
                <p style={{fontSize:10,color:"#475569"}}>65/100 required for VVB pre-qualification · All scores SSOT-derived</p>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <div style={{textAlign:"right"}}>
                  <p style={{fontSize:8,color:"#475569"}}>Overall Score</p>
                  <p style={{fontSize:24,fontWeight:900,color:carbonIntel.pdd_overall_score>=65?"#22c55e":carbonIntel.pdd_overall_score>=50?"#f59e0b":"#ef4444"}}>{carbonIntel.pdd_overall_score}/100</p>
                </div>
                <div style={{background:carbonIntel.pdd_validation_ready?"#14532d30":"#78350f30",border:`1px solid ${carbonIntel.pdd_validation_ready?"#22c55e40":"#f59e0b40"}`,borderRadius:8,padding:"8px 12px",textAlign:"center"}}>
                  <p style={{fontSize:8,color:"#475569"}}>VVB Pre-qual</p>
                  <p style={{fontSize:10,fontWeight:900,color:carbonIntel.pdd_validation_ready?"#22c55e":"#f59e0b"}}>{carbonIntel.pdd_validation_ready?"Ready":"Not ready"}</p>
                  <p style={{fontSize:8,color:"#64748b"}}>Prob: {carbonIntel.issuance_probability}</p>
                </div>
              </div>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            {Object.entries(carbonIntel.pdd_readiness||{}).map(([k,dim])=>(
              <div key={k} style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:14}} data-testid={`readiness-dim-${k}`}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                  <p style={{fontSize:10,fontWeight:700,color:"#f1f5f9"}}>{dim.label}</p>
                  <span style={{fontSize:13,fontWeight:900,color:dim.score>=65?"#22c55e":dim.score>=45?"#f59e0b":"#ef4444"}}>{dim.score}/100</span>
                </div>
                <div style={{height:4,background:"#1e293b",borderRadius:2,overflow:"hidden",marginBottom:6}}>
                  <div style={{height:"100%",width:`${dim.score}%`,background:dim.score>=65?"#22c55e":dim.score>=45?"#f59e0b":"#ef4444",borderRadius:2}}/>
                </div>
                <p style={{fontSize:9,color:"#475569"}}>{dim.note}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ASSET STRUCTURING */}
      {carbonIntel && activeSection==="structuring" && (
        <div style={{display:"flex",flexDirection:"column",gap:10}} data-testid="section-structuring">
          <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16}}>
            <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:12}}>Revenue Waterfall — Base Case ($15/tCO₂e, indicative)</p>
            {(carbonIntel.structuring?.revenue_waterfall||[]).map((row,i)=>{
              const isNet=(row[0]||"").includes("Net");
              const grossV=Math.abs(((carbonIntel.structuring?.revenue_waterfall||[])[0]||[])[1]||1);
              const bw=Math.min(100,Math.abs(row[1]||0)/grossV*100);
              return(
                <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 0",borderTop:isNet?"2px solid #1e293b":"none"}} data-testid={`waterfall-row-${i}`}>
                  <span style={{fontSize:10,fontWeight:isNet?900:400,color:"#f1f5f9"}}>{row[0]}</span>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <div style={{width:100,height:6,background:"#1e293b",borderRadius:3,overflow:"hidden"}}><div style={{height:"100%",width:`${bw}%`,background:row[2]||"#64748b",borderRadius:3}}/></div>
                    <span style={{fontSize:10,fontWeight:700,width:80,textAlign:"right",color:row[2]||"#64748b"}}>{(row[1]||0)>=0?"+":""}{fmtU(Math.abs(row[1]||0))}/yr</span>
                  </div>
                </div>
              );
            })}
            <div style={{background:"#1e3a5f30",borderRadius:6,padding:10,marginTop:8}}>
              <p style={{fontSize:9,color:"#60a5fa"}}><span style={{fontWeight:700}}>5yr Net (indicative):</span> {fmtU(carbonIntel.structuring?.net_revenue_5yr)} · Verification: ${carbonIntel.structuring?.verification_cost_tco2e}/tCO₂e</p>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            {[["SPV Readiness",carbonIntel.structuring?.spv_readiness],["Forward Offtake",carbonIntel.structuring?.forward_offtake],
              ["Blended Finance",carbonIntel.structuring?.blended_finance],["Insurance",carbonIntel.structuring?.insurance_req]
            ].map(([l,item])=>item?(
              <div key={l} style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:14}} data-testid={`struct-${l.toLowerCase().replace(/\s+/g,"-")}`}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                  <p style={{fontSize:10,fontWeight:700,color:"#f1f5f9"}}>{l}</p>
                  <span style={{fontSize:8,fontWeight:700,padding:"2px 6px",borderRadius:3,background:"#1e293b",color:item.status==="Ready"||item.status==="Applicable"?"#22c55e":"#f59e0b"}}>{item.status}</span>
                </div>
                <div style={{height:4,background:"#1e293b",borderRadius:2,overflow:"hidden",marginBottom:6}}><div style={{height:"100%",width:`${item.score}%`,background:"#60a5fa"}}/></div>
                <p style={{fontSize:9,color:"#475569"}}>{item.note}</p>
              </div>
            ):null)}
          </div>
        </div>
      )}

      {/* COMMERCIALISATION */}
      {carbonIntel && activeSection==="commercial" && (
        <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,overflow:"hidden"}} data-testid="section-commercial">
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead><tr style={{borderBottom:"1px solid #1e293b",background:"#060c1a"}}>
              {["Project","Target Buyer","Market","Structure","Priority","Readiness","Volume","Indicative Value","Next Action"].map(h=>(
                <th key={h} style={{textAlign:"left",padding:"8px 10px",fontSize:8,color:"#475569",fontWeight:600}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {(carbonIntel.commercialisation||[]).map((c,i)=>(
                <tr key={i} style={{borderBottom:"1px solid #0f172a"}} data-testid={`commercial-row-${i}`}>
                  <td style={{padding:"7px 10px",fontSize:9,color:"#f1f5f9",fontWeight:600,maxWidth:120}}><p style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.project}</p></td>
                  <td style={{padding:"7px 10px",fontSize:9,color:"#94a3b8"}}>{c.buyer}</td>
                  <td style={{padding:"7px 10px"}}><span style={{fontSize:8,fontWeight:700,padding:"2px 5px",borderRadius:3,background:c.market.includes("Art")?"#4c1d9520":"#14532d20",color:c.market.includes("Art")?"#a78bfa":"#22c55e"}}>{c.market}</span></td>
                  <td style={{padding:"7px 10px",fontSize:8,color:"#64748b"}}>{c.structure}</td>
                  <td style={{padding:"7px 10px"}}><span style={{fontSize:8,fontWeight:700,padding:"2px 5px",borderRadius:3,background:c.priority==="High"?"#14532d20":c.priority==="Medium"?"#78350f20":"#1e293b",color:c.priority==="High"?"#22c55e":c.priority==="Medium"?"#f59e0b":"#64748b"}}>{c.priority}</span></td>
                  <td style={{padding:"7px 10px"}}><span style={{fontSize:8,fontWeight:700,padding:"2px 5px",borderRadius:3,background:c.readiness==="Ready"?"#14532d20":"#78350f20",color:c.readiness==="Ready"?"#22c55e":"#f59e0b"}}>{c.readiness}</span></td>
                  <td style={{padding:"7px 10px",fontSize:9,fontWeight:700,color:"#60a5fa"}}>{fmtN(c.volume_tco2e)}</td>
                  <td style={{padding:"7px 10px"}}><p style={{fontSize:9,fontWeight:900,color:"#f1f5f9"}}>{fmtU(c.indicative_value)}/yr</p><p style={{fontSize:7,color:"#475569"}}>indicative only</p></td>
                  <td style={{padding:"7px 10px",fontSize:8,color:"#60a5fa"}}>{c.readiness==="Ready"?"Initiate DOE pre-screening":"Upgrade evidence first"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Mandatory Disclaimer */}
      {carbonIntel && (
        <div style={{background:"#7f1d1d15",border:"1px solid #ef444425",borderRadius:8,padding:14,marginTop:10}} data-testid="carbon-mandatory-disclaimer">
          <p style={{fontSize:9,fontWeight:700,color:"#ef4444",marginBottom:4}}>Mandatory Carbon Asset Origination Disclosure</p>
          <p style={{fontSize:9,color:"#f87171"}}>{qualData?.mandatory_disclaimer||"Carbon monetisation values are INDICATIVE ESTIMATES. NOT bankable. Article 6 conditional on NCCC sovereign authorisation. Not for financing commitments without formal VVB validation and registry issuance."}</p>
        </div>
      )}
    </div>
  );
}
